import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Instagram, Facebook, MessageCircle } from "lucide-react";
import TitanLogo from "@/components/TitanLogo";

// TikTok brand icon (Lucide doesn't ship one)
function TikTokIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.94a8.16 8.16 0 0 0 4.77 1.52V7a4.85 4.85 0 0 1-1.84-.31z"/>
    </svg>
  );
}

export default function Footer() {
  return (
    <footer className="bg-[#0d2040] text-white" data-testid="site-footer">
      <div className="container-titan py-16 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-4">
          <TitanLogo variant="light" size="lg" />
          <p className="mt-5 text-white/70 text-sm leading-relaxed max-w-sm">
            Ghana&apos;s premium car rental service. From economy daily drivers to luxury chauffeured SUVs —
            modeled on world-class standards, delivered with Ghanaian hospitality.
          </p>
          <div className="flex items-center gap-3 mt-6" data-testid="footer-socials">
            <a
              href="https://www.instagram.com/titancarrentalsgh"
              target="_blank"
              rel="noreferrer noopener"
              aria-label="Follow Titan Car Rentals on Instagram"
              className="w-9 h-9 flex items-center justify-center border border-white/30 hover:bg-[#e63946] hover:border-[#e63946] transition-all"
              data-testid="footer-instagram"
            >
              <Instagram size={16} />
            </a>
            <a
              href="https://www.facebook.com/titancarrentalsgh"
              target="_blank"
              rel="noreferrer noopener"
              aria-label="Follow Titan Car Rentals on Facebook"
              className="w-9 h-9 flex items-center justify-center border border-white/30 hover:bg-[#e63946] hover:border-[#e63946] transition-all"
              data-testid="footer-facebook"
            >
              <Facebook size={16} />
            </a>
            <a
              href="https://www.tiktok.com/@titancarrentalsgh"
              target="_blank"
              rel="noreferrer noopener"
              aria-label="Follow Titan Car Rentals on TikTok"
              className="w-9 h-9 flex items-center justify-center border border-white/30 hover:bg-[#e63946] hover:border-[#e63946] transition-all"
              data-testid="footer-tiktok"
            >
              <TikTokIcon size={16} />
            </a>
            <a
              href="https://wa.me/233244945582"
              target="_blank"
              rel="noreferrer noopener"
              aria-label="Chat with Titan Car Rentals on WhatsApp"
              className="w-9 h-9 flex items-center justify-center border border-white/30 hover:bg-[#25D366] hover:border-[#25D366] transition-all"
              data-testid="footer-whatsapp-icon"
            >
              <MessageCircle size={16} />
            </a>
          </div>
        </div>

        <div className="md:col-span-2">
          <h4 className="font-display text-base font-semibold mb-4 uppercase tracking-wider">Explore</h4>
          <ul className="space-y-2">
            <li><Link to="/" className="footer-link">Home</Link></li>
            <li><Link to="/fleet" className="footer-link">Fleet</Link></li>
            <li><Link to="/services" className="footer-link">Services</Link></li>
            <li><Link to="/quote" className="footer-link">Quote</Link></li>
            <li><Link to="/faq" className="footer-link">FAQ</Link></li>
            <li><Link to="/about" className="footer-link">About</Link></li>
            <li><Link to="/contact" className="footer-link">Contact</Link></li>
          </ul>
        </div>

        <div className="md:col-span-3">
          <h4 className="font-display text-base font-semibold mb-4 uppercase tracking-wider">Services</h4>
          <ul className="space-y-2">
            <li><span className="footer-link">Daily Rentals</span></li>
            <li><span className="footer-link">Long-term Leases</span></li>
            <li><span className="footer-link">Airport Pickup</span></li>
            <li><span className="footer-link">Chauffeur Service</span></li>
            <li><span className="footer-link">Corporate Accounts</span></li>
          </ul>
        </div>

        <div className="md:col-span-3">
          <h4 className="font-display text-base font-semibold mb-4 uppercase tracking-wider">Visit Us</h4>
          <ul className="space-y-3 text-sm text-white/75">
            <li className="flex gap-2"><MapPin size={16} className="text-[#e63946] mt-0.5 flex-shrink-0" /> 3 Maseru St., East Legon, Accra · Ghana</li>
            <li className="flex gap-2"><Phone size={16} className="text-[#e63946] mt-0.5 flex-shrink-0" /> 054 012 4247 / 020 123 4999</li>
            <li className="flex gap-2"><MessageCircle size={16} className="text-[#25D366] mt-0.5 flex-shrink-0" /> <a href="https://wa.me/233244945582" target="_blank" rel="noreferrer noopener" className="footer-link" data-testid="footer-whatsapp">Click for WhatsApp Chat</a></li>
            <li className="flex gap-2"><Mail size={16} className="text-[#e63946] mt-0.5 flex-shrink-0" /> info@titancarrental.com</li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="container-titan py-5 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-white/55">
          <span>© {new Date().getFullYear()} Titan Car Rentals. All rights reserved.</span>
          <span>Designed for Ghana&apos;s drivers · Built for the journey.</span>
        </div>
      </div>
    </footer>
  );
}
